import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { tshirtProducts } from '@/mocks/products';

const categories = ['All', 'Cat Shirts', 'Grammar', 'Nerdy', 'Sci-Fi', 'Humor', 'Fine Art', 'Music'];

function RevealDiv({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('ew-revealed'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`ew-reveal ${className ?? ''}`} style={style}>{children}</div>;
}

export default function TShirts() {
  const [activeCategory, setActiveCategory] = useState('All');
  const filtered = activeCategory === 'All' ? tshirtProducts : tshirtProducts.filter(s => s.category === activeCategory);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-28 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://public.readdy.ai/ai/img_res/bbfa6a4e9521aa3b78e855d4630c3ad0.jpg" alt="T-Shirts hero" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/65"></div>
          </div>
          <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
            <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.3rem' }} className="mb-2">Artist-original. Copyrighted. Hilarious.</p>
            <h1 style={{ fontFamily: '"Playfair Display", serif', color: '#fff' }} className="text-5xl md:text-6xl font-black mb-4">The Best T-Shirts<br /><em style={{ color: '#E8B96A' }}>in the Known Universe</em></h1>
            <p style={{ fontFamily: '"Lato", sans-serif', color: '#EDE5D8', lineHeight: 1.8 }} className="text-lg mb-8">Funny cat shirts. Nerdy tees. Sci-fi designs. Grammar humor. Fine art. Every design is an original, copyrighted work by an individual artist — not mass-produced, not generic.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#shop-grid" className="px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>Find Your Vibe</a>
              <a href="tel:7346468620" className="px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 border-2 border-white" style={{ color: '#fff', fontFamily: '"Lato", sans-serif', backgroundColor: 'transparent' }}>Call to Order</a>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section style={{ backgroundColor: '#1E4F4F' }} className="py-5">
          <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-6 md:gap-12">
            {['Artist-Original Designs', 'Copyrighted by Individual Artists', 'Free Shipping on Orders $75+ (Code: FREE-SHIPPING)', 'Not Mass-Produced'].map(t => (
              <span key={t} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide whitespace-nowrap" style={{ fontFamily: '"Lato", sans-serif', color: '#E8B96A' }}>
                <i className="ri-star-fill text-xs"></i> {t}
              </span>
            ))}
          </div>
        </section>

        {/* Grid */}
        <section id="shop-grid" className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-12">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#C9933A', fontSize: '1.2rem' }}>Something for every sense of humor</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-4xl font-bold mt-1 mb-3">Browse Our T-Shirt Collection</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42' }} className="text-base">
                {tshirtProducts.length} unique designs — call or email to check availability and place your order.
              </p>
            </RevealDiv>

            {/* Category filter pills */}
            <div className="flex flex-wrap gap-3 justify-center mb-10">
              {categories.map(c => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className="px-4 py-2 rounded-full text-sm font-bold cursor-pointer transition-all duration-200 whitespace-nowrap"
                  style={{
                    backgroundColor: activeCategory === c ? '#C9933A' : '#EDE5D8',
                    color: activeCategory === c ? '#fff' : '#4A3728',
                    fontFamily: '"Lato", sans-serif',
                  }}
                >
                  {c} {c !== 'All' && `(${tshirtProducts.filter(s => s.category === c).length})`}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {filtered.map((shirt, i) => (
                <RevealDiv key={shirt.id} style={{ transitionDelay: `${(i % 8) * 0.06}s` }}>
                  <div className="rounded-2xl overflow-hidden group cursor-pointer h-full flex flex-col" style={{ backgroundColor: '#fff' }}>
                    <div className="overflow-hidden flex-shrink-0" style={{ height: '260px' }}>
                      <img src={shirt.img} alt={shirt.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <span className="text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-full self-start" style={{ backgroundColor: '#EDE5D8', color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>{shirt.category}</span>
                      <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-base font-bold mt-2 mb-1 flex-1">{shirt.title}</h3>
                      <p style={{ fontFamily: '"Lato", sans-serif', color: '#C9933A', fontWeight: 700 }} className="text-sm mb-3">{shirt.price}</p>
                      <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center gap-1.5 text-sm font-bold whitespace-nowrap transition-colors duration-200 hover:text-[#A07020]" style={{ color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>
                        Inquire to Order <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                </RevealDiv>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-16">
                <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42' }}>No shirts in this category yet.</p>
              </div>
            )}
          </div>
        </section>

        {/* Artist note */}
        <RevealDiv>
          <section className="py-16" style={{ backgroundColor: '#F5F0E8' }}>
            <div className="max-w-3xl mx-auto px-4 text-center">
              <i className="ri-award-line text-4xl mb-4 block" style={{ color: '#C9933A' }}></i>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-3xl font-bold mb-4">Every Design is an Original</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42', lineHeight: 1.8 }} className="text-base mb-6">
                Every t-shirt in our collection features a design copyrighted by the individual artist who created it. You won&apos;t find these at big box stores. When you wear an Earth Wisdom™ tee, you&apos;re wearing real art — and supporting real artists.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:7346468620" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#1E4F4F', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-phone-line"></i> Call Us
                </a>
                <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-mail-line"></i> Email Us
                </a>
              </div>
            </div>
          </section>
        </RevealDiv>
      </main>
      <Footer />
    </div>
  );
}
